import * as React from 'react';

import { cn } from '@/lib/utils';

export interface DataCardField {
  label: string;
  value: React.ReactNode;
  className?: string;
}

export interface DataCardProps {
  header: React.ReactNode;
  fields: DataCardField[];
  trailing?: React.ReactNode;
  footer?: React.ReactNode;
  onClick?: () => void;
  selected?: boolean;
  highlighted?: boolean;
  striped?: boolean;
  index?: number;
  className?: string;
}

const NO_ROW_CLICK_ATTR = 'data-no-row-click';

export function DataCard({
  header,
  fields,
  trailing,
  footer,
  onClick,
  selected,
  highlighted,
  striped,
  index = 0,
  className,
}: DataCardProps) {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onClick) return;
    const target = event.target as HTMLElement;
    if (target.closest(`[${NO_ROW_CLICK_ATTR}]`)) {
      return;
    }
    onClick();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const isStripedAlt = striped && index % 2 === 1;
  const interactive = Boolean(onClick);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Card acts as a row button when interactive; role and tabIndex are set conditionally.
    <div
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={cn(
        'rounded-lg border bg-card text-card-foreground shadow-sm p-4 space-y-3',
        interactive &&
          'cursor-pointer transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isStripedAlt && 'bg-muted/30',
        selected && 'ring-2 ring-primary ring-offset-0',
        highlighted && 'border-l-4 border-l-amber-400 bg-amber-50',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">{header}</div>
        {trailing ? <div className="flex shrink-0 items-start gap-1">{trailing}</div> : null}
      </div>

      {fields.length > 0 ? (
        <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] gap-x-3 gap-y-2 text-sm">
          {fields.map((field) => (
            <React.Fragment key={field.label}>
              <dt className="text-muted-foreground">{field.label}</dt>
              <dd className={cn('text-[#051923] break-words', field.className)}>{field.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}

      {footer ? <div className="pt-1">{footer}</div> : null}
    </div>
  );
}
